import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ConnectionStatus } from '@/components/ConnectionStatus'
import { ReaderBrand, ReaderBrandName } from '@/features/library/ReaderBrand'
import { readReaderRoute, readerRouteHash } from '@/services/readerNavigation'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ChangePasswordView } from '@/features/auth/ChangePasswordView'
import { LoginView } from '@/features/auth/LoginView'
import { LibraryView } from '@/features/library/LibraryView'
import { StudentDashboard } from '@/features/library/StudentDashboard'
import { TasksView } from '@/features/exercises/TasksView'
import { ReaderView } from '@/features/reader/ReaderView'
import { useSessionBootstrap } from '@/hooks/useSessionBootstrap'
import { logout } from '@/services/authService'
import { resolveLoginContext } from '@/services/loginContext'
import { useAuthStore } from '@/stores/authStore'

/** Vem do link de acesso da escola, lembrado no aparelho (RN-18/RN-20). */
const { schoolId: SCHOOL_ID, tenantSlug: TENANT_SLUG } = resolveLoginContext()

type Tela =
  | { nome: 'login' }
  | { nome: 'senha'; matricula: string }
  | { nome: 'biblioteca' }
  | { nome: 'avaliacoes' }
  | { nome: 'leitor'; assetId: string }

export function App() {
  const [telaEscolhida, setTelaState] = useState<Tela>({ nome: 'login' })
  const usuario = useAuthStore((s) => s.user)
  const sessao = useSessionBootstrap()
  const queryClient = useQueryClient()
  useEffect(() => useAuthStore.subscribe((current, previous) => {
    if (current.user?.id !== previous.user?.id) queryClient.clear()
  }), [queryClient])

  function setTela(next: Tela) {
    setTelaState(next)
    const hash = next.nome === 'login' ? '#/login' : next.nome === 'senha' ? '#/primeiro-acesso'
      : readerRouteHash(next)
    if (window.location.hash !== hash) window.history.pushState(null, '', hash)
  }
  useEffect(() => {
    const navigate = () => {
      if (usuario && !usuario.must_change_password) setTelaState(readReaderRoute(window.location.hash))
    }
    window.addEventListener('popstate', navigate)
    window.addEventListener('hashchange', navigate)
    return () => {
      window.removeEventListener('popstate', navigate)
      window.removeEventListener('hashchange', navigate)
    }
  }, [usuario])

  // Sessão restaurada do refresh token guardado: o aluno volta direto para a
  // biblioteca em vez de logar de novo a cada reload. DERIVADO, não efeito com
  // setState — o efeito renderizava o login antes de trocar para a biblioteca.
  //
  // ⚠️ `usuario !== null` não é redundância. `sessao` é resultado do bootstrap e
  // não muda mais depois dele: sem esta condição, o logout limpava o store,
  // pedia a tela de login e a derivação devolvia o aluno à biblioteca no mesmo
  // render — com o nome vazio no cabeçalho e sem sessão nenhuma por trás.
  const tela: Tela =
    telaEscolhida.nome === 'login' &&
    sessao === 'autenticado' &&
    usuario !== null &&
    !usuario.must_change_password
      ? readReaderRoute(window.location.hash)
      : telaEscolhida

  if (sessao === 'verificando') {
    return <p role="status">Carregando…</p>
  }
  if (sessao === 'indisponivel') {
    return <section role="alert"><p>Não foi possível conectar. Sua sessão foi preservada.</p>
      <button onClick={() => window.dispatchEvent(new Event('lumina:retry-session'))}>Tentar novamente</button>
    </section>
  }

  return (
    <ErrorBoundary>
      <ConnectionStatus />
      {usuario && !usuario.must_change_password && <ReaderBrand />}
      {tela.nome === 'login' && (
        <LoginView
          schoolId={SCHOOL_ID}
          tenantSlug={TENANT_SLUG}
          onSuccess={(precisaTrocar, matricula) =>
            setTela(precisaTrocar ? { nome: 'senha', matricula } : { nome: 'biblioteca' })
          }
        />
      )}

      {tela.nome === 'senha' && (
        <ChangePasswordView
          matricula={tela.matricula}
          schoolId={SCHOOL_ID}
          tenantSlug={TENANT_SLUG}
          onDone={() => setTela({ nome: 'biblioteca' })}
        />
      )}

      {tela.nome === 'biblioteca' && (
        <>
          <header className="app-topbar">
            <ReaderBrandName />
            <span>{usuario?.name}</span>
            <button type="button" onClick={() => setTela({ nome: 'avaliacoes' })}>Minhas avaliações</button>
            <button
              type="button"
              onClick={() => {
                // `logout()` nunca rejeita (ver authService): a sessão local
                // some mesmo sem rede, então o `then` sempre corre.
                void logout().then(() => setTela({ nome: 'login' }))
              }}
            >
              Sair
            </button>
          </header>
          <StudentDashboard onOpen={(item) => setTela({ nome: 'leitor', assetId: item.asset_id })} />
          <LibraryView
            onOpen={(item) =>
              setTela({
                nome: 'leitor',
                assetId: item.asset_id,
              })
            }
          />
        </>
      )}

      {tela.nome === 'leitor' && (
        <ReaderView assetId={tela.assetId} onClose={() => setTela({ nome: 'biblioteca' })} />
      )}

      {tela.nome === 'avaliacoes' && <TasksView onClose={() => setTela({ nome: 'biblioteca' })} />}

    </ErrorBoundary>
  )
}
